import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useWebNotification } from '../hooks/useWebNotification';

// Staff roles that should be prompted to enable push. Customers already have
// their own "Enable notifications" banner in CustomerSelect.
const STAFF_ROLES = new Set([
  'super_admin', 'admin', 'handler',
  'deposit_handler', 'withdrawal_handler', 'support_handler',
  'inquiry_handler', 'third_party_withdraw_handler', 'calling_handler',
]);

const DISMISS_KEY = 'dws_notif_prompt_dismissed';

// Global, portal-rendered popup shown once per session (i.e. on every login —
// sessionStorage is cleared on logout) for staff whose browser has NOT granted
// notification permission. Fixes background push never arriving because
// admins/handlers never subscribed. Renders above every dashboard view branch
// (desktop AND the separate mobile-WhatsApp views) since it's a body portal.
const NotificationPermissionPrompt = () => {
  const { user } = useAuth();
  const { permission, requestPermission } = useWebNotification();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let dismissed = false;
    try { dismissed = sessionStorage.getItem(DISMISS_KEY) === '1'; } catch (_) {}
    const eligible =
      !!user &&
      STAFF_ROLES.has(user.role) &&
      permission !== 'granted' &&
      !dismissed;
    if (!eligible) { setVisible(false); return; }
    const t = setTimeout(() => setVisible(true), 900);
    return () => clearTimeout(t);
  }, [user, permission]);

  if (!visible) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (_) {}
    setVisible(false);
  };

  const onAllow = async () => {
    const p = await requestPermission();
    if (p === 'granted') {
      toast.success('Notifications enabled — you will now get message alerts.');
      setVisible(false);
    } else {
      toast.error('Notifications blocked. Enable them in your browser/site settings.');
      dismiss();
    }
  };

  return createPortal(
    <div
      className="fixed inset-x-0 top-0 z-[100000] flex justify-center px-3 pt-3 pointer-events-none"
      data-testid="notif-permission-prompt"
    >
      <div className="pointer-events-auto w-full max-w-md rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden animate-in slide-in-from-top-4 fade-in duration-300">
        <div className="flex items-start gap-3 p-4">
          <div className="flex-shrink-0 w-11 h-11 rounded-full bg-[#128C7E]/10 flex items-center justify-center">
            <Bell size={22} weight="fill" className="text-[#128C7E]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-semibold text-gray-900 leading-snug">
              Enable notifications
            </p>
            <p className="text-[13px] text-gray-500 mt-0.5 leading-snug">
              Get instant alerts for new messages even when this app is in the background.
            </p>
          </div>
          <button
            onClick={dismiss}
            data-testid="notif-permission-close"
            className="flex-shrink-0 -mt-1 -mr-1 p-1.5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="flex gap-2 px-4 pb-4">
          <button
            onClick={dismiss}
            data-testid="notif-permission-decline"
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Decline
          </button>
          <button
            onClick={onAllow}
            data-testid="notif-permission-allow"
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#128C7E] hover:bg-[#0e6e63] transition-colors"
          >
            Allow
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NotificationPermissionPrompt;
